import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { BookOpen, GraduationCap, UserCheck, FileSignature, Award, Wallet, StampIcon, Plane, ArrowUpRight } from "lucide-react";

const steps = [
  { n: "01", t: "Test Coaching",       d: "GRE · TOEFL · GMAT · IELTS · PTE · Duolingo — taught by educators who know the tests inside-out.", icon: BookOpen,        slug: "test-coaching" },
  { n: "02", t: "Course Selection",    d: "Pick a course that fits you — and your career goal, not a template.",                              icon: GraduationCap,   slug: "course-selection" },
  { n: "03", t: "Profile Building",    d: "Make admission committees say yes the first time you apply.",                                     icon: UserCheck,       slug: "profile-building" },
  { n: "04", t: "University Shortlist", d: "Smart shortlists. Cleaner applications. Higher admit rates.",                                    icon: Award,           slug: "university-shortlist" },
  { n: "05", t: "Document Design",      d: "SOPs, LORs, resumes — crafted to actually convert.",                                             icon: FileSignature,   slug: "document-design" },
  { n: "06", t: "Scholarship & Loan",   d: "Maximise your scholarship; minimise loan stress.",                                               icon: Wallet,          slug: "scholarship-loan" },
  { n: "07", t: "Visa Counselling",     d: "Embassy interview prep, documentation, mock sessions.",                                          icon: StampIcon,       slug: "visa-counselling" },
  { n: "08", t: "Pre-Departure",        d: "Land in your new country already feeling at home.",                                              icon: Plane,           slug: "pre-departure" },
];

export default function Services() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start center", "end end"] });
  const pathLength = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section
      id="services"
      ref={ref}
      className="py-20 md:py-28 bg-[#282829] text-white relative overflow-hidden"
      data-testid="services-section"
    >
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{
        backgroundImage: "radial-gradient(#fff 1px, transparent 1px)",
        backgroundSize: "26px 26px"
      }} />

      <div className="relative max-w-6xl mx-auto px-6 sm:px-10">
        <div className="text-center mb-14">
          <span className="inline-block bg-[#c92e34] text-white text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full border-2 border-white -rotate-2">
            The Journey · 8 Steps
          </span>
          <h2 className="font-display text-5xl md:text-7xl mt-4 leading-[0.9]">
            The full <span className="text-[#c92e34]">stack</span> for<br />going abroad.
          </h2>
        </div>

        {/* Journey path — spine */}
        <div className="relative">
          {/* Spine: centered on desktop, left on mobile */}
          <div className="absolute top-0 bottom-0 left-6 md:left-1/2 md:-translate-x-1/2 w-[3px] pointer-events-none">
            <div className="absolute inset-0 border-l-[3px] border-dashed border-white/20" />
            <motion.div
              style={{ scaleY: pathLength, transformOrigin: "top" }}
              className="absolute inset-0 bg-[#c92e34] w-[3px]"
            />
          </div>

          <div className="space-y-10 md:space-y-16">
            {steps.map((s, i) => {
              const Icon = s.icon;
              const isLeft = i % 2 === 0;
              return (
                <motion.div
                  key={s.t}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="relative"
                  data-testid={`service-card-${i}`}
                >
                  {/* Center / spine node */}
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ type: "spring", stiffness: 220, delay: 0.15 }}
                    className="absolute top-2 left-6 md:left-1/2 -translate-x-1/2 w-12 h-12 md:w-16 md:h-16 rounded-full border-2 border-white bg-[#c92e34] flex items-center justify-center z-10"
                  >
                    <Icon size={22} strokeWidth={2.4} className="md:w-[26px] md:h-[26px]" />
                    <span className="absolute inset-0 rounded-full border-2 border-[#c92e34] animate-ping opacity-50" />
                  </motion.div>

                  {/* Card — mobile: right of spine; desktop: alternating */}
                  <div
                    className={[
                      "ml-20 md:ml-0 md:grid md:grid-cols-2 md:gap-12 items-center",
                    ].join(" ")}
                  >
                    <motion.div
                      initial={{ opacity: 0, x: isLeft ? -20 : 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 }}
                      whileHover={s.slug ? { scale: 1.02, y: -3 } : {}}
                      className={[
                        "bg-white text-[#282829] border-2 border-[#282829] rounded-3xl p-5 relative",
                        s.slug ? "cursor-pointer brutal-shadow-red-sm hover:brutal-shadow-red transition-shadow" : "",
                        isLeft ? "md:text-right md:pr-8 md:mr-0" : "md:order-2 md:pl-8 md:ml-0",
                      ].join(" ")}
                    >
                      {s.slug ? (
                        <Link
                          to={`/service/${s.slug}`}
                          className="absolute inset-0 rounded-3xl"
                          aria-label={`Open ${s.t} details`}
                          data-testid={`service-link-${s.n}`}
                        />
                      ) : null}
                      <div className={`flex items-center gap-2 ${isLeft ? "md:flex-row-reverse" : ""}`}>
                        <span className="font-anton text-4xl md:text-5xl text-[#c92e34] leading-none">{s.n}</span>
                        <div className="h-px flex-1 bg-[#282829]/15" />
                        {s.slug && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-widest uppercase bg-[#c92e34] text-white px-2 py-1 rounded-full border-2 border-[#282829]">
                            Open <ArrowUpRight size={11} />
                          </span>
                        )}
                      </div>
                      <h3 className="font-display text-2xl md:text-3xl mt-2 leading-tight">{s.t}</h3>
                      <p className="mt-2 text-sm text-[#282829]/65">{s.d}</p>
                    </motion.div>

                    {/* Badge */}
                    <div className={isLeft ? "hidden md:block" : "hidden md:block md:order-1"}>
                      <div
                        className={[
                          "inline-flex items-center gap-3 bg-[#c92e34] text-white border-2 border-white rounded-full px-5 py-3",
                          isLeft ? "" : "md:ml-auto md:mr-0",
                        ].join(" ")}
                      >
                        <Icon size={18} strokeWidth={2.5} />
                        <span className="font-anton tracking-widest uppercase text-sm">Step {s.n}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Finish sticker */}
          <motion.div
            initial={{ opacity: 0, scale: 0.6, rotate: 0 }}
            whileInView={{ opacity: 1, scale: 1, rotate: -4 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 180 }}
            className="mt-14 flex justify-center"
          >
            <div className="bg-[#c92e34] text-white font-display text-xl md:text-3xl px-7 py-5 rounded-full border-2 border-white text-center">
              ✈ You're abroad. Let's go.
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
